<script setup lang="ts">
import type { Route } from '@/@types/gtfs-ressource';
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    // An on-demand route carries the same badge fields as a GTFS one.
    route: Pick<Route, 'route_short_name' | 'route_color' | 'route_text_color'>;
    size?: 'medium' | 'small';
  }>(),
  {
    size: 'small',
  },
);

const isRouteColorWhiteOrEmpty = computed(() => {
  const routeColor = props.route.route_color;
  return !routeColor || routeColor.toUpperCase() === 'FFFFFF';
});
</script>

<template>
  <span
    class="route-badge"
    :class="`route-badge--${props.size}`"
    :style="{
      'background-color': `#${props.route.route_color || 'FFFFFF'}`,
      color: `#${props.route.route_text_color || '000000'}`,
      border: `${isRouteColorWhiteOrEmpty ? '1px solid #000' : 'none'}`,
    }"
  >
    <span class="route-badge__label">{{ route.route_short_name }}</span>
  </span>
</template>

<style lang="scss">
.route-badge {
  align-items: center;
  display: inline-flex;
  justify-content: center;
  letter-spacing: $letter-spacing-snug;
  max-width: 8em;
  width: fit-content;
  text-align: center;
  white-space: nowrap;

  // `route_short_name` is free-form GTFS text: clip it, otherwise `nowrap` makes
  // a long name spill outside the badge instead of stopping at `max-width`.
  // The clip box is grown vertically (and pulled back by the margin) so the
  // 1:1 line-height does not cut accents and descenders.
  &__label {
    margin: -4px 0;
    min-width: 0;
    overflow: hidden;
    padding: 4px 0;
    text-overflow: ellipsis;
  }

  &--medium {
    border-radius: $radius-tag;
    font: 600 14px / 1 $font-poppins;
    padding: $spacing-2 $spacing-3;
  }

  // 24px tall in the mockups (cap-height trimmed text): 6px around a 12px line.
  &--small {
    border-radius: $radius-tag-small;
    font: 600 12px / 1 $font-poppins;
    padding: 6px $spacing-2;
  }
}
</style>
